import { ITag } from './getFirebaseTag';

/**
 * Compute a fuzzy‑match score between `query` and `target`.
 * Treats `query` as a subsequence to find in `target`.
 *
 * Lower scores indicate tighter/earlier matches; Infinity means no match.
 *
 * @param query - the subsequence to search for
 * @param target - the string to search within
 * @returns numeric score (lower is better) or Infinity if not a match
 */
export function fizzyScore(query: string, target: string): number {
  let cumulativeScore = 0;
  let queryIndex = 0;

  for (let targetIndex = 0; targetIndex < target.length && queryIndex < query.length; targetIndex++) {
    const targetChar = target[targetIndex].toLowerCase();
    const queryChar = query[queryIndex].toLowerCase();

    if (targetChar === queryChar) {
      // reward matches occurring sooner & closer together
      cumulativeScore += targetIndex - queryIndex;
      queryIndex++;
    }
  }

  return queryIndex === query.length ? cumulativeScore : Infinity;
}

/**
 * Generic fuzzy‑search helper.
 * Filters and sorts an array of items by how well they match `query`.
 *
 * @typeParam T - type of items to search
 * @param query - the search string
 * @param items - array of items to filter
 * @param getTarget - function to extract the searchable string from each item
 * @param onScore - optional callback invoked with each computed score
 * @returns items that match, sorted by best match first
 */
export function fuzzySearch<GenericItem>(
  query: string,
  items: GenericItem[],
  getTarget: (item: GenericItem) => string,
  onScore?: (score: number) => void,
): GenericItem[] {
  if (!query) {
    return items;
  }

  const scoredItems = items.map((item) => {
    const text = getTarget(item);
    const score = fizzyScore(query, text);
    if (onScore) onScore(score);

    return { item, score };
  });

  const filteredItems = scoredItems.filter(({ score }) => Number.isFinite(score));

  const sortedItems = filteredItems.sort((a, b) => a.score - b.score).map(({ item }) => item);

  return sortedItems;
}

/**
 * Fuzzy‑search an array of strings.
 *
 * @param query - the search string
 * @param items - list of strings to search
 * @returns filtered & sorted strings
 */
export function fizzySearch(query: string, items: string[]): string[] {
  return fuzzySearch(query, items, (text) => text);
}

/**
 * Fuzzy‑search an array of ITag objects by their `label`.
 * Logs each computed score via the standard Logger.
 *
 * @param query - the search string
 * @param items - list of tags to search
 * @returns filtered & sorted tags
 */
export function fizzySearchTags(query: string, items: ITag[]): ITag[] {
  return fuzzySearch(query, items, (tag) => tag.label);
}
